import type { ThumbnailProps } from '@shopify/polaris';

import { THUMB_PRODUCT_DEFAULT } from '~/constants/thumbnail';
import { classNames } from '~/presentation/common';

export interface GThumbnailProps extends ThumbnailProps {
  defaultSource?: string;
  width?: string;
  height?: string;
  classRemoved?: boolean;
  isInactive?: boolean;
  /**
   * @description Filter to apply grayscale to the image
   */
  grayscale?: `${number}%`;
  /**
   * @description Keeps the source ratio inside the square box instead of stretching it to fill
   */
  objectFit?: 'contain';
}

/**
 * Helper function to generate a variation class name.
 * @param name - Base name of the class.
 * @param value - Variation value to append.
 * @returns Combined class name.
 */
export function variationName(name: string, value: string): string {
  return `${name}${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

/**
 * Custom Thumbnail component with error handling and dynamic sizing.
 * @param props - Component properties including source, alt text, size, defaultSource, width, and height.
 * @returns JSX Element.
 */
export const GThumbnail = ({
  source,
  alt,
  size,
  grayscale,
  objectFit,
  defaultSource = THUMB_PRODUCT_DEFAULT,
  width = '80',
  height = '80',
  classRemoved = false,
}: GThumbnailProps) => {
  const sizeClass = size && variationName('Polaris-Thumbnail--size', size);
  const filterClass = grayscale && 'Polaris-GThumbnail--filter';
  const className = classNames('Polaris-Thumbnail', sizeClass);

  const thumbnail = (
    <img
      onError={(e) => {
        e.currentTarget.src = defaultSource;
      }}
      alt={alt ?? 'Thumbnail'}
      src={`${source ?? defaultSource}`}
      width={width}
      height={height}
      style={
        {
          '--gp-grayscale-percentage': grayscale || '100%',
          objectFit,
        } as React.CSSProperties
      }
      className={classNames('Polaris-GThumbnail', filterClass)}
    />
  );

  if (classRemoved) return thumbnail;

  return <span className={className}>{thumbnail}</span>;
};
